import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Truck, Users, Shield, BarChart3, ClipboardList, FileCheck,
  MapPin, Zap, ChevronRight, ArrowLeft, User
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Widget Components (same as HubDashboard)
function TasksWidget() {
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-blue-400" />
          </div>
          <h3 className="font-bold">My Tasks</h3>
        </div>
        <span className="text-xs bg-blue-500/20 text-blue-400 px-2.5 py-1 rounded-full font-medium">3 active</span>
      </div>
      <div className="space-y-2">
        {[
          { text: 'Complete PTI for Unit 404', color: 'red' },
          { text: 'Deliver to Chicago Hub', color: 'yellow' },
          { text: 'Fuel stop at Station 7', color: 'green' },
        ].map((task, idx) => (
          <div key={idx} className={`p-3 rounded-xl bg-secondary/40 border-l-4 flex items-center justify-between ${
            task.color === 'red' ? 'border-l-red-500' : task.color === 'yellow' ? 'border-l-yellow-500' : 'border-l-green-500'
          }`}>
            <p className="text-sm font-medium">{task.text}</p>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </div>
        ))}
      </div>
    </div>
  );
}

function TrucksWidget() {
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
            <Truck className="w-5 h-5 text-amber-400" />
          </div>
          <h3 className="font-bold">My Trucks</h3>
        </div>
      </div>
      <div className="space-y-3">
        {[
          { unit: '404', status: 'moving', location: 'I-90 Chicago', fuel: 75 },
          { unit: '218', status: 'parked', location: 'Hub A', fuel: 45 },
          { unit: '512', status: 'maintenance', location: 'Shop', fuel: 90 },
        ].map((truck, idx) => (
          <div key={idx} className="p-3 rounded-xl bg-secondary/40">
            <div className="flex justify-between items-center mb-2">
              <span className="font-bold">Unit {truck.unit}</span>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                truck.status === 'moving' ? 'bg-green-500/20 text-green-400' :
                truck.status === 'parked' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
              }`}>{truck.status}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mb-2">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{truck.location}</span>
              <span className="flex items-center gap-1"><Zap className="w-3 h-3" />{truck.fuel}%</span>
            </div>
            <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${truck.fuel > 60 ? 'bg-green-500' : truck.fuel > 30 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{ width: `${truck.fuel}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsWidget() {
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-purple-400" />
        </div>
        <h3 className="font-bold">Quick Stats</h3>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 bg-green-500/15 rounded-xl border border-green-500/20">
          <p className="text-xl font-bold text-green-400">+125</p>
          <p className="text-xs text-green-400/70">This Week</p>
        </div>
        <div className="text-center p-3 bg-purple-500/15 rounded-xl border border-purple-500/20">
          <p className="text-xl font-bold text-purple-400">3</p>
          <p className="text-xs text-purple-400/70">Referrals</p>
        </div>
        <div className="text-center p-3 bg-accent/15 rounded-xl border border-accent/20">
          <p className="text-xl font-bold text-accent">#5</p>
          <p className="text-xs text-accent/70">Rank</p>
        </div>
      </div>
    </div>
  );
}

function DriversWidget() {
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
          <Users className="w-5 h-5 text-green-400" />
        </div>
        <h3 className="font-bold">Drivers</h3>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 bg-green-500/15 rounded-xl border border-green-500/20">
          <p className="text-2xl font-bold text-green-400">8</p>
          <p className="text-xs text-green-400/70">On Duty</p>
        </div>
        <div className="text-center p-3 bg-yellow-500/15 rounded-xl border border-yellow-500/20">
          <p className="text-2xl font-bold text-yellow-400">4</p>
          <p className="text-xs text-yellow-400/70">Off Duty</p>
        </div>
        <div className="text-center p-3 bg-secondary/30 rounded-xl">
          <p className="text-2xl font-bold">12</p>
          <p className="text-xs text-muted-foreground">Total</p>
        </div>
      </div>
    </div>
  );
}

function SafetyWidget() {
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
          <Shield className="w-5 h-5 text-red-400" />
        </div>
        <h3 className="font-bold">Safety Alerts</h3>
      </div>
      <div className="space-y-2">
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <p className="text-sm font-medium">Unit 404 - Brake Inspection</p>
          <span className="text-xs text-red-400 font-medium">URGENT</span>
        </div>
        <div className="p-3 rounded-xl bg-secondary/40">
          <p className="text-sm">Unit 218 - Tire Check</p>
          <span className="text-xs text-yellow-400">PENDING</span>
        </div>
      </div>
    </div>
  );
}

function ComplianceWidget() {
  return (
    <div className="bg-card/60 backdrop-blur-xl rounded-3xl p-5 border border-border/30 h-full">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
          <FileCheck className="w-5 h-5 text-purple-400" />
        </div>
        <h3 className="font-bold">PTI Compliance</h3>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 bg-green-500/15 rounded-xl border border-green-500/20">
          <p className="text-2xl font-bold text-green-400">8</p>
          <p className="text-xs text-green-400/70">Done</p>
        </div>
        <div className="text-center p-3 bg-yellow-500/15 rounded-xl border border-yellow-500/20">
          <p className="text-2xl font-bold text-yellow-400">2</p>
          <p className="text-xs text-yellow-400/70">Pending</p>
        </div>
        <div className="text-center p-3 bg-red-500/15 rounded-xl border border-red-500/20">
          <p className="text-2xl font-bold text-red-400">1</p>
          <p className="text-xs text-red-400/70">Failed</p>
        </div>
      </div>
    </div>
  );
}

const WIDGET_COMPONENTS = {
  tasks: TasksWidget,
  trucks: TrucksWidget,
  stats: StatsWidget,
  drivers: DriversWidget,
  safety: SafetyWidget,
  compliance: ComplianceWidget,
};

export default function PublicBoard() {
  const { boardId } = useParams();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBoard = async () => {
      try {
        const res = await axios.get(`${API}/boards/${boardId}`);
        setBoard(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Board not found');
      }
      setLoading(false);
    };
    fetchBoard();
  }, [boardId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <img src="/logo-full.png" alt="Leader Hub" className="h-24 w-24 mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Loading board...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <img src="/logo-full.png" alt="Leader Hub" className="h-24 w-24 mx-auto mb-4 opacity-30" />
          <h1 className="text-2xl font-bold mb-2">Board Not Found</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link to="/login">
            <Button>Go to Login</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-xl border-b border-border/30 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </Link>
            <img src="/logo-full.png" alt="Leader Hub" className="h-10 w-10 object-contain" />
          </div>
          <div className="text-right">
            <h1 className="font-bold text-lg">{board?.name || 'Dashboard'}</h1>
            <p className="text-sm text-muted-foreground flex items-center gap-1 justify-end">
              <User className="w-3 h-3" />
              {board?.user_name || 'Anonymous'}
            </p>
          </div>
        </div>
      </header>

      {/* Board Content */}
      <main className="max-w-6xl mx-auto p-4 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {board?.widgets?.map((widget, idx) => {
            const WidgetComponent = WIDGET_COMPONENTS[widget.type];
            if (!WidgetComponent) return null;
            return (
              <div key={widget.id || idx}>
                <WidgetComponent />
              </div>
            );
          })}
        </div>

        {(!board?.widgets || board.widgets.length === 0) && (
          <div className="text-center py-12 text-muted-foreground">
            <p>This board has no widgets.</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-border/30 p-4 text-center text-sm text-muted-foreground">
        Powered by <span className="text-accent font-semibold">Leader Hub</span>
      </footer>
    </div>
  );
}
